import { useEffect, useRef, useState } from "react";
import { GlassPanel, UtilityButton } from "@guandan/ui";
import type {
  RemoteSpectatorAuthority,
  SpectatorConnectionState,
  SpectatorEvent,
  SpectatorFrame,
  SpectatorSeat,
} from "../game/spectator-authority";
import type { PublicRoomView, SpectatorGateway } from "../lib/platform-api";

interface SpectatorRoomProps {
  readonly gateway: SpectatorGateway;
  readonly room: PublicRoomView;
  readonly password?: string;
  readonly onExit: () => void;
}

const seatNames = ["一号位", "二号位", "三号位", "四号位"] as const;

export function SpectatorRoom({
  gateway,
  room,
  password,
  onExit,
}: SpectatorRoomProps) {
  const authorityRef = useRef<RemoteSpectatorAuthority | null>(null);
  const authorityKeyRef = useRef("");
  const cleanupTimerRef = useRef<number | null>(null);
  const [frame, setFrame] = useState<SpectatorFrame | null>(null);
  const [connection, setConnection] =
    useState<SpectatorConnectionState>("connecting");
  const [error, setError] = useState("");
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    let disposed = false;
    if (cleanupTimerRef.current !== null) {
      window.clearTimeout(cleanupTimerRef.current);
      cleanupTimerRef.current = null;
    }
    const authorityKey = `${room.id}\u0000${password ?? ""}`;
    let authority = authorityRef.current;
    if (!authority || authorityKeyRef.current !== authorityKey) {
      if (authority) void authority.close().catch(() => undefined);
      authority = gateway.open({
        roomId: room.id,
        ...(password ? { password } : {}),
      });
      authorityRef.current = authority;
      authorityKeyRef.current = authorityKey;
    }
    const unsubscribe = authority.subscribe((next) => {
      if (!disposed) setFrame(next);
    });
    const unsubscribeStatus = authority.subscribeStatus((next) => {
      if (!disposed) setConnection(next);
    });
    void authority
      .connect()
      .then((next) => {
        if (!disposed) setFrame(next);
      })
      .catch((cause: unknown) => {
        if (!disposed)
          setError(messageOf(cause, "无法进入只读观战，请返回后重试"));
      });
    return () => {
      disposed = true;
      unsubscribe();
      unsubscribeStatus();
      cleanupTimerRef.current = window.setTimeout(() => {
        if (authorityRef.current !== authority) return;
        authorityRef.current = null;
        authorityKeyRef.current = "";
        void authority.close().catch(() => undefined);
      }, 0);
    };
  }, [gateway, password, room.id]);

  async function exitSpectating() {
    setExiting(true);
    setError("");
    try {
      const authority = authorityRef.current;
      await authority?.close();
      if (authorityRef.current === authority) {
        authorityRef.current = null;
        authorityKeyRef.current = "";
      }
      onExit();
    } catch (cause) {
      setError(messageOf(cause, "退出观战失败，请重试"));
      setExiting(false);
    }
  }

  const recentEvents = frame ? [...frame.events].slice(-12).reverse() : [];
  return (
    <main className="sub-screen spectator-screen">
      <header className="sub-header">
        <div>
          <span>服务器只读公开视角</span>
          <h1>{room.name}</h1>
        </div>
        <UtilityButton disabled={exiting} onClick={() => void exitSpectating()}>
          {exiting ? "正在退出…" : "退出观战"}
        </UtilityButton>
      </header>
      <GlassPanel className="spectator-panel">
        <div className="spectator-heading">
          <div>
            <strong>房号 {room.code}</strong>
            <span>{connectionLabel(connection)}</span>
          </div>
          <p>
            只显示四席公开状态与公开动作，不包含任何玩家手牌、身份
            ID、语音或牌桌操作。
          </p>
        </div>
        {error ? (
          <p className="form-error" role="alert">
            {error}
          </p>
        ) : null}
        {!frame && !error ? (
          <div className="spectator-loading" role="status">
            正在获取服务器公开牌局…
          </div>
        ) : null}
        {frame ? (
          <>
            <section className="spectator-meta" aria-label="公开牌局状态">
              <span>
                当前级牌 <strong>{frame.snapshot.levelRank}</strong>
              </span>
              <span>
                {frame.snapshot.phase === "settled"
                  ? "牌局已结算"
                  : frame.snapshot.phase === "tribute"
                    ? "正在进还贡"
                    : frame.snapshot.paused
                      ? "牌局已暂停"
                      : frame.snapshot.currentSeat === null
                        ? "等待服务器推进"
                        : `${seatName(frame.snapshot.currentSeat)}行动中`}
              </span>
              <span>
                {frame.snapshot.lastPlay
                  ? `${seatName(frame.snapshot.lastPlay.seat)} · ${frame.snapshot.lastPlay.combination} · ${frame.snapshot.lastPlay.cardIds.length} 张`
                  : "本轮尚未出牌"}
              </span>
            </section>
            <section className="spectator-seats" aria-label="四席公开信息">
              {frame.snapshot.players.map((player) => (
                <article
                  className={
                    player.seat === frame.snapshot.currentSeat
                      ? "is-current"
                      : ""
                  }
                  key={player.publicId}
                >
                  <span className="spectator-seat-index">
                    {seatName(player.seat)} ·{" "}
                    {player.team === 0 ? "东南队" : "西北队"}
                  </span>
                  <strong>{player.nickname}</strong>
                  <span>
                    {player.isBot ? "AI 玩家" : "真人玩家"} ·{" "}
                    {player.connected ? "在线" : "连接中断"}
                  </span>
                  <b>
                    {player.finishOrder
                      ? `第 ${player.finishOrder} 名完牌`
                      : `剩余 ${player.remainingCards} 张`}
                  </b>
                </article>
              ))}
            </section>
            <section
              className="spectator-events"
              aria-labelledby="spectator-events-title"
            >
              <div>
                <h2 id="spectator-events-title">最近公开动作</h2>
                <span>只读 · 自动同步</span>
              </div>
              {recentEvents.length ? (
                <ol>
                  {recentEvents.map((event) => (
                    <li key={`${event.seq}-${event.type}`}>
                      <time>{formatEventTime(event.createdAt)}</time>
                      <span>{describeEvent(event)}</span>
                    </li>
                  ))}
                </ol>
              ) : (
                <p>暂无新的公开动作</p>
              )}
            </section>
          </>
        ) : null}
      </GlassPanel>
    </main>
  );
}

function describeEvent(event: SpectatorEvent): string {
  switch (event.type) {
    case "GAME_STARTED":
      return "牌局开始";
    case "ROUND_STARTED":
      return event.payload.firstSeat === null ||
        event.payload.firstSeat === undefined
        ? `第 ${event.payload.roundNumber} 局开始，${seatName(event.payload.dealerSeat)}首抓，贡还牌后确定首出`
        : `第 ${event.payload.roundNumber} 局开始，${seatName(event.payload.firstSeat)}首出`;
    case "CARDS_PLAYED":
      return `${seatName(event.payload.seat)}打出 ${event.payload.cardIds.length} 张`;
    case "PLAYER_PASSED":
      return `${seatName(event.payload.seat)}不出`;
    case "PLAYER_FINISHED":
      return `${seatName(event.payload.seat)}第 ${event.payload.order} 名完牌`;
    case "TRICK_RESET":
      return `新一轮由${seatName(event.payload.leadSeat)}领出`;
    case "ROUND_SETTLED":
      return `第 ${event.payload.roundNumber} 局结算 · ${teamName(event.payload.winningTeam)}升 ${event.payload.advancement} 级`;
    case "LEVEL_ADVANCED":
      return `${teamName(event.payload.winningTeam)}从 ${event.payload.levelBefore} 升至 ${event.payload.levelAfter}`;
    case "ACE_RESET":
      return `${teamName(event.payload.team)}连续三把未过 A，退回 2`;
    case "TRIBUTE_OFFERED":
      return `${seatName(event.payload.from)}完成进贡`;
    case "TRIBUTE_RECEIVED":
      return `${seatName(event.payload.from)}向${seatName(event.payload.to)}进贡`;
    case "TRIBUTE_RETURNED":
      return `${seatName(event.payload.from)}向${seatName(event.payload.to)}还贡`;
    case "ANTI_TRIBUTE":
      return `${event.payload.donors.map(seatName).join("、")}抗贡`;
    case "MATCH_SETTLED":
      return `牌局结束 · ${teamName(event.payload.winningTeam)}获胜`;
    case "GAME_PAUSED":
      return `${seatName(event.payload.seat)}申请暂停`;
    case "GAME_RESUMED":
      return `${seatName(event.payload.seat)}继续牌局`;
    case "CONTROL_CHANGED":
      return `${seatName(event.payload.seat)}切换为${controlName(event.payload.control)}`;
    case "QUICK_PHRASE":
      return `${seatName(event.payload.seat)}发送全桌快捷语`;
    case "EMOTE":
      return `${seatName(event.payload.seat)}发送全桌表情`;
  }
}

function seatName(seat: number): string {
  return seatNames[seat as SpectatorSeat];
}

function teamName(team: number): string {
  return team === 0 ? "东南队" : "西北队";
}

function controlName(
  control: "human" | "bot" | "bot-takeover" | "pending-human-return" | "empty",
): string {
  if (control === "human") return "真人控制";
  if (control === "pending-human-return") return "等待真人恢复";
  if (control === "empty") return "空座";
  return "AI 托管";
}

function connectionLabel(state: SpectatorConnectionState): string {
  if (state === "live") return "实时只读连接";
  if (state === "polling") return "安全轮询同步";
  if (state === "closed") return "观战已结束";
  return "正在建立只读连接";
}

function formatEventTime(createdAt: number): string {
  return new Intl.DateTimeFormat("zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(createdAt);
}

function messageOf(cause: unknown, fallback: string): string {
  return cause instanceof Error && cause.message ? cause.message : fallback;
}
